import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import { acceptDuelInvite, declineDuelInvite, getMyDuelInvites } from '../../api/duels';
import '../../css/chats/DuelInviteAttachment.css';

/**
 * Парсит текст сообщения-приглашения и извлекает данные
 */
function parseInviteMessage(text) {
    const lines = text?.split('\n') || [];
    const data = {
        duelName: '',
        duelId: '',
        senderName: '',
        format: '',
        role: '',
        rounds: '',
        message: ''
    };

    lines.forEach(line => {
        if (line.includes('📛 Дуэль:')) {
            data.duelName = line.split('📛 Дуэль:')[1]?.trim() || '';
        } else if (line.includes('👤 От:')) {
            data.senderName = line.split('👤 От:')[1]?.trim() || '';
        } else if (line.includes('🎮 Формат:')) {
            data.format = line.split('🎮 Формат:')[1]?.trim() || '';
        } else if (line.includes('🏆 Роль:')) {
            data.role = line.split('🏆 Роль:')[1]?.trim() || '';
        } else if (line.includes('⏰ Раундов:')) {
            data.rounds = line.split('⏰ Раундов:')[1]?.trim() || '';
        } else if (line.includes('💬 Сообщение:')) {
            data.message = line.split('💬 Сообщение:')[1]?.trim() || '';
        }
    });

    return data;
}

/**
 * Находит приглашение по ID дуэли или названию
 */
async function findInvite(duelId, duelName) {
    try {
        const invites = await getMyDuelInvites();
        if (!Array.isArray(invites)) return null;

        // Сначала ищем по ID дуэли (более надёжно)
        if (duelId) {
            const byDuelId = invites.find(inv =>
                inv.status === 'PENDING' &&
                inv.duel?.id === duelId
            );
            if (byDuelId) return byDuelId;
        }

        // Если не нашли, ищем по названию
        if (duelName) {
            const byName = invites.find(inv =>
                inv.status === 'PENDING' &&
                inv.duel?.title?.toLowerCase().includes(duelName.toLowerCase())
            );
            if (byName) return byName;
        }

        return null;
    } catch (err) {
        console.error('[DuelInviteFromMessage] Failed to find invite:', err);
        return null;
    }
}

/**
 * Компонент для отображения приглашения из текстового сообщения
 */
export default function DuelInviteFromMessage({ message, isOwn, onInviteAction }) {
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const parsed = parseInviteMessage(message.content || message.text);
    
    // Пытаемся получить данные из вложений сообщения
    const inviteAttachment = message.invitationAttachments?.find(
        att => att.invitationType === 'DUEL_INVITE'
    );
    const inviteIdFromAttachment = inviteAttachment?.inviteId;
    const duelIdFromAttachment = inviteAttachment?.entityId;

    const formatLabels = {
        PARTICIPANT: 'Участник',
        JUDGE: 'Судья',
        'участником': 'Участник',
        'судьёй': 'Судья'
    };

    const handleAccept = async () => {
        setActionLoading(true);
        setError(null);

        try {
            // Если есть inviteId из вложения, используем его
            if (inviteIdFromAttachment) {
                await acceptDuelInvite(inviteIdFromAttachment);
                setSuccess(true);
                if (onInviteAction) onInviteAction('accept', inviteIdFromAttachment);
                return;
            }

            // Иначе ищем приглашение по ID дуэли или названию
            const invite = await findInvite(duelIdFromAttachment, parsed.duelName);
            if (!invite) {
                setError('Не удалось найти приглашение. Попробуйте принять на странице дуэли.');
                setActionLoading(false);
                return;
            }

            await acceptDuelInvite(invite.id);
            setSuccess(true);
            if (onInviteAction) onInviteAction('accept', invite.id);
        } catch (err) {
            console.error('Ошибка при принятии приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось принять приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDecline = async () => {
        setActionLoading(true);
        setError(null);

        try {
            // Если есть inviteId из вложения, используем его
            if (inviteIdFromAttachment) {
                await declineDuelInvite(inviteIdFromAttachment);
                setSuccess(true);
                if (onInviteAction) onInviteAction('decline', inviteIdFromAttachment);
                return;
            }

            // Иначе ищем приглашение по ID дуэли или названию
            const invite = await findInvite(duelIdFromAttachment, parsed.duelName);
            if (!invite) {
                setError('Не удалось найти приглашение. Попробуйте отклонить на странице дуэли.');
                setActionLoading(false);
                return;
            }

            await declineDuelInvite(invite.id);
            setSuccess(true);
            if (onInviteAction) onInviteAction('decline', invite.id);
        } catch (err) {
            console.error('Ошибка при отклонении приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось отклонить приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    if (success) {
        return (
            <div className="duel-invite-attachment duel-invite-attachment--success">
                <HrnbAlert variant="success" onClose={() => {}}>
                    Приглашение обработано!
                </HrnbAlert>
            </div>
        );
    }

    return (
        <div className="duel-invite-attachment">
            <div className="duel-invite-attachment__header">
                <span className="duel-invite-attachment__icon">🎤</span>
                <h4 className="duel-invite-attachment__title">Приглашение в дуэль</h4>
            </div>

            <div className="duel-invite-attachment__body">
                {parsed.duelName && (
                    <div className="duel-invite-attachment__duel">
                        <span className="duel-invite-attachment__duel-label">Дуэль:</span>
                        <span className="duel-invite-attachment__duel-name">{parsed.duelName}</span>
                    </div>
                )}

                {parsed.senderName && (
                    <div className="duel-invite-attachment__sender">
                        <span className="duel-invite-attachment__sender-label">От:</span>
                        <span className="duel-invite-attachment__sender-name">{parsed.senderName}</span>
                    </div>
                )}

                <div className="duel-invite-attachment__details">
                    {parsed.format && (
                        <div className="duel-invite-attachment__detail">
                            <span>🎮 {parsed.format}</span>
                        </div>
                    )}
                    {parsed.role && (
                        <div className="duel-invite-attachment__detail">
                            <span>🏆 Роль: {formatLabels[parsed.role] || parsed.role}</span>
                        </div>
                    )}
                    {parsed.rounds && (
                        <div className="duel-invite-attachment__detail">
                            <span>⏰ {parsed.rounds}</span>
                        </div>
                    )}
                </div>

                {parsed.message && (
                    <div className="duel-invite-attachment__message">
                        <em>{parsed.message}</em>
                    </div>
                )}
            </div>

            {error && (
                <HrnbAlert variant="warning" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {!isOwn && !success && (
                <div className="duel-invite-attachment__actions">
                    <HrnbButton
                        variant="success"
                        size="sm"
                        onClick={handleAccept}
                        disabled={actionLoading || !parsed.duelName}
                    >
                        {actionLoading ? 'Принятие...' : 'Принять'}
                    </HrnbButton>
                    <HrnbButton
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDecline}
                        disabled={actionLoading || !parsed.duelName}
                    >
                        {actionLoading ? 'Отклонение...' : 'Отклонить'}
                    </HrnbButton>
                </div>
            )}

            {isOwn && (
                <div className="duel-invite-attachment__status">
                    <span className="duel-invite-attachment__status-text">
                        Ожидает ответа
                    </span>
                </div>
            )}

            {!success && (
                <div className="duel-invite-attachment__note">
                    <Link to="/duels" className="duel-invite-attachment__link">
                        Перейти к дуэлям →
                    </Link>
                </div>
            )}
        </div>
    );
}
